import axios from 'axios';

import { athenaBaseUrl, POST } from '../../../utils/axios-interceptor';
import { saveDocMetaDataInDocService } from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { getGlobalAuthHeaders } from '../../globalAskAica/service';
import { CHAT_CONFIG } from './service';
import { ClaimAttachmentsRequest, ClaimAttachmentsResponse } from './types';

const CHAT_ATTACHMENTS_URL = `${athenaBaseUrl}/chat/attachments`;
const MNEMOSYNE_CHAT_FLOW_ID = 16;

// Fresh instance so the app-wide default `x-organization-id` header never rides along:
// the portfolio surface must send none, and the leads one sets it explicitly.
const attachmentAxios = axios.create();
delete attachmentAxios.defaults.headers['x-organization-id'];

/** PUT a file to a presigned S3 URL with the given content type, reporting 0-100 progress. The shared uploadFileApi stays progress-less. */
export const uploadToS3WithProgress = (
    url: string,
    file: File,
    contentType: string,
    onProgress: (percent: number) => void,
): Promise<void> =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        // The presigned URL does not sign a content type, so the stored object gets exactly this label.
        if (contentType) xhr.setRequestHeader('Content-Type', contentType);
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`S3 upload failed with status ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error during S3 upload'));
        xhr.send(file);
    });

interface SaveChatFileMetadataParams {
    file: File;
    /** Never `application/octet-stream` — see resolveAttachmentMimeType. */
    contentType: string;
    categoryId: number | string;
    mnemosyneOrgId?: string;
    assesseeOrgId?: string;
    uploadedBy: string;
}

/**
 * Registers the uploaded file as a NEW mnemosyne doc. Always called — never the duplicate-reuse branch —
 * because the backend's ownership gate needs every chat attachment to be a freshly created doc.
 * Resolves the doc id, or rejects with mnemosyne's message.
 */
export const saveChatFileMetadata = async ({
    file,
    contentType,
    categoryId,
    mnemosyneOrgId,
    assesseeOrgId,
    uploadedBy,
}: SaveChatFileMetadataParams) => {
    const docData = await saveDocMetaDataInDocService({
        input: {
            categoryId,
            mnemosyneOrgId,
            fileName: file.name,
            fileSizeKb: file.size / 1_000,
            flowId: MNEMOSYNE_CHAT_FLOW_ID,
            fileType: contentType,
            // Binds the claim to THIS user: on portfolio the org header is the PNO for every user of it.
            uploadedBy,
        },
        // Raw: coercing undefined to '' sends an empty organization-id header and breaks mnemosyne's fallback.
        orgId: assesseeOrgId,
    });
    if (!docData?.id) throw new Error(docData?.responseData?.responseMessage || 'Error in uploading file');
    return String(docData.id);
};

/** Leads sends the company org; portfolio sends none. */
const buildHeaders = (userId: string, orgId?: string) =>
    orgId ? CHAT_CONFIG.getHeaders(orgId, userId) : getGlobalAuthHeaders(null, userId);

export const claimAttachments = async (
    body: ClaimAttachmentsRequest,
    userId: string,
    orgId?: string,
): Promise<ClaimAttachmentsResponse> => {
    const response = await attachmentAxios({ method: POST, url: CHAT_ATTACHMENTS_URL, headers: buildHeaders(userId, orgId), data: body });
    return response.data;
};
